import React, { useState } from 'react';
import { Field as FinalField } from 'react-final-form';
import { PhoneInput, PhoneInputProps } from 'react-international-phone';
import { CountryData, CountryIso2, ParsedCountry } from 'react-international-phone/build/types';
import type { FieldState } from 'final-form';
import parsePhoneNumber, { CountryCode } from 'libphonenumber-js/max';
import { get, upperCase } from 'lodash';
import { createGlobalStyle } from 'styled-components';
import { getRadius } from '@lendi-ui/commons/borders';
import getColour from '@lendi-ui/commons/colours';
import Field from '@lendi-ui/field';
import { InputProp } from '@lendi-ui/text-input';

import { useFormValues } from '@funnels/hooks';
import { InternationalPhoneInputCustomWrapper } from '@funnels/ui-components/Common/InternationalPhoneInput/react-international-phone.style';
import { getAriaLabelById, validateNotEmptyString } from '@funnels/utils';

import { FieldLabel } from '../_common/FieldLabel';
import { CommonFieldProps } from '../_common/types';

interface FieldProps {
  name: string;
  format?: (value: any) => any;
  parse?: (value: any) => any;
  validate?: (value: any, allValues?: Object, FieldMeta?: FieldState<any>) => any;
}
export interface TextInputFieldProps extends CommonFieldProps, FieldProps, Omit<InputProp, 'name' | 'onChange'> {
  fixedLength?: number;
  labelAfter?: React.ReactNode;
  className?: string;
}

export interface InternationalMobileTextInputFieldProps
  extends CommonFieldProps,
    FieldProps,
    Omit<InputProp, 'name' | 'onChange'>,
    Omit<PhoneInputProps, 'onFocus' | 'onBlur'> {
  disabled?: boolean;
  isDisabled?: boolean;
  name: string;
  labelText?: string;
  showAllowedList?: boolean;
  autoFocus?: boolean;
  forceDialCode?: boolean;
  fixedLength?: number;
  labelAfter?: React.ReactNode;
  className?: string;
  countryList: CountryData[];
  onCountryChange?: (countryCode: CountryCode) => void;
}

const GlobalStyle = createGlobalStyle<{ isError: boolean }>`
  :root {
    --react-international-phone-height: calc(3 * var(--lendi-ui-size));
    --react-international-phone-dropdown-item-height: calc(3 * var(--lendi-ui-size));
    --react-international-phone-border-color: ${({ isError }) =>
      isError ? getColour('interaction', 'error') : getColour('border', 'interaction')};
    --react-international-phone-border-width: ${({ isError }) => (isError ? '2px' : '1px')};
    --react-international-phone-border-radius: ${getRadius()};
    --react-international-phone-font-size: var(--lendi-ui-size);
    --react-international-phone-selected-dropdown-item-background-color:${getColour('fill', 'selected')};;
    --react-international-phone-text-color: ${getColour('text', 'main')};
    --react-international-phone-selected-dropdown-item-text-color: ${getColour('text', 'main')};
    --react-international-phone-dropdown-item-dial-code-color: ${getColour('text', 'main')};
    --react-international-phone-dropdown-item-text-color: ${getColour('text', 'main')};
    --react-international-phone-flag-width: 34px;
    --react-international-phone-flag-height: 26px;
  }
`;

const InternationalMobileTextInputField = (props: InternationalMobileTextInputFieldProps) => {
  const {
    format,
    isDisabled,
    isOptional,
    label,
    labelAfter,
    name,
    parse,
    subLabel,
    validate,
    validationFailMessage,
    disableFailMessage = false,
    'aria-labelledby': ariaLabelledby,
    'aria-describedby': ariaDescribedby,
    showAllowedList,
    autoFocus = false,
    forceDialCode = false,
    onCountryChange = () => undefined,
    countryList = [],
  } = props;

  const fieldProps: FieldProps = {
    name,
    parse,
    format,
  };

  const [country, setCountry] = useState<CountryCode>('AU');

  if (!isDisabled) {
    if (validate) {
      fieldProps.validate = validate;
    } else {
      fieldProps.validate = validateNotEmptyString(validationFailMessage, isOptional);
    }
  }

  const handleCountryChange = (value: CountryCode) => {
    if (country === value) return;
    setCountry(value);
    onCountryChange(value);
  };

  const otherProps = {
    ...(showAllowedList && { countries: countryList }),
  };

  const [formValues] = useFormValues();

  const currentPhoneNumber = get(formValues, name);

  return (
    <InternationalPhoneInputCustomWrapper>
      <FieldLabel label={label} labelAfter={labelAfter} subLabel={subLabel} idPostfix={name} isOptional={isOptional} />
      <FinalField {...fieldProps}>
        {({ input, meta }) => (
          <>
            <GlobalStyle isError={!!(meta.error && meta.touched)} />
            <Field {...meta} error={disableFailMessage ? undefined : meta.error}>
              <PhoneInput
                value={currentPhoneNumber}
                inputClassName="phoneInput fs-exclude"
                dialCodePreviewStyleProps={{ className: 'dialCode' }}
                countrySelectorStyleProps={{
                  buttonClassName: 'countrySelectorButton',
                  className: 'countrySelector',
                  dropdownStyleProps: {
                    className: 'countrySelectorDropDown',
                    listItemClassName: 'countrySelectorDropDownListItem',
                    listItemFlagClassName: 'countrySelectorDropDownListItemFlag',
                  },
                  dropdownArrowClassName: 'countrySelectorDropdownArrow',
                }}
                forceDialCode={forceDialCode}
                inputProps={{
                  autoFocus,
                  ...input,
                }}
                disabled={isDisabled}
                onChange={(phone: string, meta: { country: ParsedCountry; inputValue: string }) => {
                  const countryCode: CountryCode = (meta.country ? upperCase(meta.country.iso2) : 'AU') as CountryCode;
                  handleCountryChange(countryCode);
                  input.onChange(phone);
                }}
                {...otherProps}
                defaultCountry="au"
                aria-labelledby={getAriaLabelById('label', label ? name : undefined, ariaLabelledby)}
                aria-describedby={getAriaLabelById('description', subLabel ? name : undefined, ariaDescribedby)}
                aria-invalid={disableFailMessage ? false : meta.error && meta.touched}
              />
            </Field>
          </>
        )}
      </FinalField>
    </InternationalPhoneInputCustomWrapper>
  );
};

export default InternationalMobileTextInputField;
